import { CalculatorOutlined, FilePdfOutlined } from "@ant-design/icons";
import CurrencyFormat from "../../../../hook/CurrencyFormat";
import {
  Button,
  Col,
  Form,
  InputNumber,
  message,
  Modal,
  Row,
  Spin,
  Table,
  Tabs,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import {
  HEADERS_EXPORT_DETAIL,
  POST_CALCULATE_IRR,
  POST_CALCULATE_SOD,
  POST_CALCULATE_WEIGHTED,
} from "../../../API/apiUrls";
import axios from "axios";

const { Text } = Typography;

// ── Mini Stat ──────────────────────────────────────────────────
const MiniStat = ({ label, value, color }) => (
  <div style={{ textAlign: "center", padding: "8px 20px", flex: 1 }}>
    <div style={{ fontSize: 11, color: "#aaa", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 17, fontWeight: 800, color }}>{value}</div>
  </div>
);

// ── Schedule Table (Ant Design Table) ─────────────────────────
const ScheduleTable = ({ data, currencyFormatPoint }) => {
  const totalInterest = data.reduce((s, r) => s + (r.interest ?? 0), 0);
  const totalPrincipal = data.reduce((s, r) => s + (r.principal_paid ?? 0), 0);

  const columns = [
    {
      title: "งวดที่",
      dataIndex: "month",
      align: "center",
      width: 70,
      render: (v) => (
        <span style={{ fontWeight: 600, color: "#555" }}>{v}</span>
      ),
    },
    {
      title: "เงินต้นยกมา",
      dataIndex: "begin_principal",
      align: "right",
      render: (v) => (
        <span style={{ color: "#333" }}>{currencyFormatPoint(v)}</span>
      ),
    },
    {
      title: "ดอกเบี้ย",
      dataIndex: "interest",
      align: "right",
      render: (v) => (
        <span style={{ color: "#e74c3c", fontWeight: 600 }}>
          {currencyFormatPoint(v)}
        </span>
      ),
    },
    {
      title: "ตัดเงินต้น",
      dataIndex: "principal_paid",
      align: "right",
      render: (v) => (
        <span style={{ color: "#3498db", fontWeight: 600 }}>
          {currencyFormatPoint(v)}
        </span>
      ),
    },
  ];

  const tableSummary = () => (
    <Table.Summary.Row style={{ background: "#f0f4f8" }}>
      <Table.Summary.Cell index={0} align="center">
        <span style={{ fontWeight: 700, color: "#2c3e50" }}>
          รวม {data.length} งวด
        </span>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1} align="right">
        <span style={{ color: "#555", fontWeight: 700 }}>—</span>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={2} align="right">
        <span style={{ color: "#e74c3c", fontWeight: 700 }}>
          {currencyFormatPoint(totalInterest)}
        </span>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={3} align="right">
        <span style={{ color: "#3498db", fontWeight: 700 }}>
          {currencyFormatPoint(totalPrincipal)}
        </span>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  return (
    <Table
      size="small"
      columns={columns}
      dataSource={data}
      rowKey="month"
      pagination={false}
      summary={tableSummary}
      rowClassName={(_, i) => (i % 2 === 0 ? "irr-row-even" : "irr-row-odd")}
      style={{ borderRadius: 8, overflow: "hidden" }}
    />
  );
};

// ── Method Tab Content ─────────────────────────────────────────
const MethodTab = ({ result, currencyFormatPoint }) => {
  if (!result)
    return (
      <div
        style={{
          textAlign: "center",
          color: "#bbb",
          padding: "40px 0",
          fontSize: 14,
        }}
      >
        กดคำนวณเพื่อดูผลลัพธ์
      </div>
    );

  return (
    <>
      {/* Summary chips */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #eef0f6",
          marginBottom: 16,
          paddingBottom: 12,
        }}
      >
        <MiniStat
          label="Total Interest"
          value={currencyFormatPoint(result.total_interest)}
          color="#5b6ef5"
        />
        <div style={{ width: "1px", background: "#eef0f6", margin: "4px 0" }} />
        <MiniStat
          label="Effective Rate (%/yr)"
          value={`${result.effective_rate} %`}
          color="#2ecc71"
        />
        <div style={{ width: "1px", background: "#eef0f6", margin: "4px 0" }} />
        <MiniStat
          label="Flat Rate (%/yr)"
          value={`${result.flat_rate} %`}
          color="#3498db"
        />
      </div>

      <ScheduleTable
        data={result.installments}
        currencyFormatPoint={currencyFormatPoint}
      />
    </>
  );
};

// ── Main Component ─────────────────────────────────────────────
const ManualIRR = ({ open, close, record }) => {
  const [, , currencyFormatPoint] = CurrencyFormat();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({
    sod: null,
    weighted: null,
    irr: null,
  });

  // watch form values สำหรับ hint
  const principal = Form.useWatch("principal", form) ?? 0;
  const monthlyPayment = Form.useWatch("monthly_payment", form) ?? 0;
  const totalMonths = Form.useWatch("total_months", form) ?? 0;
  const flatRate = Form.useWatch("flat_rate", form) ?? 0;

  const totalPay = monthlyPayment * totalMonths;
  const excess = totalPay - principal;
  const autoTarget = parseFloat(((principal * flatRate) / 100).toFixed(2));

  useEffect(() => {
    if (open && record) {
      form.setFieldsValue({
        principal: record.principal ?? 0,
        monthly_payment: record.monthly_payment ?? 0,
        total_months: record.total_months ?? 0,
        flat_rate: 5,
        max_annual_rate: record.max_annual_rate ?? 10,
        total_interest_target: autoTarget,
      });
      setResults({ sod: null, weighted: null, irr: null });
    }
  }, [open, record]);

  const handleCancel = () => {
    setResults({ sod: null, weighted: null, irr: null });
    close();
  };

  const onCalculate = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        principal: values.principal,
        monthly_payment: values.monthly_payment,
        total_months: values.total_months,
        flat_rate: values.flat_rate,
        max_annual_rate: values.max_annual_rate,
        max_interest_pct: values.max_annual_rate,
        total_interest_target: values.total_interest_target,
      };

      const [resSod, resWeighted, resIrr] = await Promise.all([
        axios.post(POST_CALCULATE_SOD, payload, {
          headers: HEADERS_EXPORT_DETAIL,
        }),
        axios.post(POST_CALCULATE_WEIGHTED, payload, {
          headers: HEADERS_EXPORT_DETAIL,
        }),
        axios.post(POST_CALCULATE_IRR, payload, {
          headers: HEADERS_EXPORT_DETAIL,
        }),
      ]);

      setResults({
        sod: resSod.data ?? null,
        weighted: resWeighted.data ?? null,
        irr: resIrr.data ?? null,
      });
    } catch (err) {
      if (err?.errorFields) return;
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: "100%", height: 40, borderRadius: 8 };

  const tabItems = [
    {
      key: "sod",
      label: (
        <span style={{ fontWeight: 700 }}>
          Sum of the Digits
          <br />
          <small style={{ fontWeight: 400, color: "#aaa" }}>
            ดอกเบี้ยสูงไปต่ำ
          </small>
        </span>
      ),
      children: (
        <MethodTab
          result={results.sod}
          currencyFormatPoint={currencyFormatPoint}
        />
      ),
    },
    {
      key: "weighted",
      label: (
        <span style={{ fontWeight: 700 }}>
          Increasing Interest
          <br />
          <small style={{ fontWeight: 400, color: "#aaa" }}>
            ดอกเบี้ยต่ำไปสูง
          </small>
        </span>
      ),
      children: (
        <MethodTab
          result={results.weighted}
          currencyFormatPoint={currencyFormatPoint}
        />
      ),
    },
    {
      key: "irr",
      label: (
        <span style={{ fontWeight: 700 }}>
          IRR Method
          <br />
          <small style={{ fontWeight: 400, color: "#aaa" }}>
            Effective Interest Rate
          </small>
        </span>
      ),
      children: (
        <MethodTab
          result={results.irr}
          currencyFormatPoint={currencyFormatPoint}
        />
      ),
    },
  ];

  return (
    <>
      <style>{`
        .irr-row-even td { background-color: #ffffff !important; }
        .irr-row-odd  td { background-color: #fafbff !important; }
      `}</style>

      <Modal
        title={
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>
              สูตรการคำนวณดอกเบี้ย
            </div>
            <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
              เปรียบเทียบ 3 วิธี: ดอกเบี้ยสูงไปต่ำ / ดอกเบี้ยต่ำไปสูง / IRR
            </div>
          </div>
        }
        open={open}
        onCancel={handleCancel}
        width="90%"
        style={{ top: 16 }}
        styles={{ body: { padding: "16px 24px" } }}
        footer={null}
      >
        <Spin spinning={loading} tip="กำลังคำนวณ...">
          {/* ── Input Card ─────────────────────────────────────── */}
          <div
            style={{
              border: "1px solid #e8ecf5",
              borderRadius: 14,
              padding: "20px 24px",
              marginBottom: 20,
              background: "#fff",
            }}
          >
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#1a1a2e",
                marginBottom: 16,
              }}
            >
              กรอกค่าที่ต้องการหา
            </div>

            <Form form={form} layout="vertical">
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item
                    name="principal"
                    label="เงินต้น"
                    rules={[{ required: true, message: "กรุณากรอกเงินต้น" }]}
                  >
                    <InputNumber
                      style={inputStyle}
                      formatter={(v) =>
                        `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(v) => v.replace(/,/g, "")}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="monthly_payment"
                    label="ยอดส่งต่องวด 1 ถึง (N-1)"
                    rules={[{ required: true, message: "กรุณากรอกค่างวด" }]}
                  >
                    <InputNumber
                      style={inputStyle}
                      formatter={(v) =>
                        `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(v) => v.replace(/,/g, "")}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="total_months"
                    label="จำนวนงวด"
                    rules={[{ required: true, message: "กรุณากรอกจำนวนงวด" }]}
                  >
                    <InputNumber style={inputStyle} min={1} />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="flat_rate"
                    label="เพดานดอกเบี้ย (% ต่อปี)"
                    rules={[
                      { required: true, message: "กรุณากรอกอัตราดอกเบี้ย" },
                    ]}
                  >
                    <InputNumber
                      style={inputStyle}
                      min={0}
                      max={100}
                      step={0.5}
                      addonAfter="%"
                      size="large"
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="max_annual_rate"
                    label="กฎดอกเบี้ยสูงสุด (% ต่อปี)"
                    rules={[{ required: true, message: "กรุณากรอกกฎดอกเบี้ย" }]}
                  >
                    <InputNumber
                      style={inputStyle}
                      min={0}
                      max={100}
                      step={0.5}
                      addonAfter="%"
                      size="large"
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="total_interest_target"
                    label="เป้าหมายดอกเบี้ยรวม"
                  >
                    <InputNumber
                      style={inputStyle}
                      formatter={(v) =>
                        `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(v) => v.replace(/,/g, "")}
                    />
                  </Form.Item>
                  {/* ── Hints ── */}
                  {autoTarget > 0 && (
                    <div
                      style={{
                        marginBottom: 12,
                        fontSize: 12,
                      }}
                    >
                      <Text type="secondary">
                        เพดานแนะนำ:{" "}
                        <Text strong style={{ color: "#2c3e50" }}>
                          {currencyFormatPoint(autoTarget)}
                        </Text>{" "}
                        (เพดาน {flatRate} % ต่อปี)
                      </Text>
                      {excess > 0 && (
                        <div style={{ color: "#5b6ef5", marginTop: 2 }}>
                          ℹ️ ค่างวดรวม {currencyFormatPoint(totalPay)}{" "}
                          เกินเงินต้น → ดอกเบี้ยจากส่วนเกิน ={" "}
                          <Text strong style={{ color: "#5b6ef5" }}>
                            {currencyFormatPoint(excess)}
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                </Col>
              </Row>

              {/* ── Action Buttons ── */}
              <div style={{ textAlign: "center", marginTop: 8 }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<CalculatorOutlined />}
                  onClick={onCalculate}
                  style={{
                    background: "#5b6ef5",
                    border: "none",
                    borderRadius: 10,
                    padding: "0 40px",
                    height: 46,
                    fontSize: 15,
                    fontWeight: 600,
                    marginRight: 12,
                    boxShadow: "0 4px 14px rgba(91,110,245,0.3)",
                  }}
                >
                  คำนวณและเปรียบเทียบ
                </Button>
                <Button
                  size="large"
                  icon={<FilePdfOutlined />}
                  disabled={!results.sod}
                  style={{
                    background: results.sod ? "#c0392b" : undefined,
                    color: results.sod ? "#fff" : undefined,
                    border: "none",
                    borderRadius: 10,
                    padding: "0 32px",
                    height: 46,
                    fontSize: 15,
                    fontWeight: 600,
                  }}
                >
                  ดาวน์โหลด PDF
                </Button>
              </div>
            </Form>
          </div>

          {/* ── Result Tabs ────────────────────────────────────── */}
          {(results.sod || results.weighted || results.irr) && (
            <Tabs
              items={tabItems}
              size="large"
              style={{ marginTop: 4 }}
              tabBarStyle={{ fontWeight: 600 }}
            />
          )}
        </Spin>
      </Modal>
    </>
  );
};

export default ManualIRR;
